// IMPORT REACT
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mediaTypeMap, notificationTemplateForMap } from "../../shared/data";
import parse from "html-react-parser";
// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PreferencesContext } from "../../PreferenceContext";
import { CustomSelect } from "../../components/CustomFields/CustomSelect";
import { PAGE_SIZE } from "../../services/common/const";
import { GetDepartmentList } from "../../services/DepartmentService";
import { GetSpecialityList } from "../../services/SpecialityService";
import {
  CreateNotificationTemplate,
  GetNotificationTemplateDetail,
  UpdateNotificationTemplate,
} from "../../services/NotificationTemplateService";
import TipTapTextEditor from "../../components/TipTapEditor/TipTapTextEditor";
import { Editor } from "@tiptap/react";
import { selectOption } from "../../services/models/Context";
import SimpleRichBox, { SimpleRichBoxHandle } from "../../components/CustomTextbox/SimpleRichBox";
import { GetNotificationTemplateDBTableFieldList } from "../../services/NotificationTemplateDBTableFieldsService";

// SCHEMA
interface NotificationTemplateDetail {
  notificationTemplateDetailId: number;
  notificationTemplateId: number;
  languageCode: string;
  languageName: string;
  templateData: string;
  isDeleted: boolean;
  mode: string;
}

// SCHEMA FOR FORM
interface NotificationTemplate {
  notificationTemplateId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  templateName: String;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  mediaType: string;
  templateFor: string;
  templateData: string;
  isDeleted: boolean;
  isLocked: boolean;
  isDefault: boolean;
}


// SCHEMA FOR PARAMETER
interface Parameter {
  specialityId: string;
  departmentId: string;
  branchId: string;
  orgId: string;
  consumerId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialityId: "",
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  orgId: "",
  consumerId: "",
};

interface DataItem {
  notificationTemplateDBTableFieldId: number;
  notificationTemplateType: string;
  underModule: string;
  displayOrder: number;
  displayName: string;

}

const availableLinks = [
  "PRESCRIPTION_LINK",
  "REPORT_CERTIFICATE_LINK",
  "GROWTH_CHART_LINK",
  "FULL_PRESCRIPTION_LINK",
  "BILLING_LINK",
];

// DEFAULT SCHEMA VALUE FOR FORM

export default function NotificationTemplateSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = Props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [editor, setEditor] = useState<string>('');
  const location = useLocation();

  const [param, setParam] = useState(parameters);
  const [specialityData, setSpecialityData] = useState<any[]>([]);
  const [departmentData, setDepartmentData] = useState<any[]>([]);
  const [resultDataCollection, setResultDataCollection] = useState<DataItem[]>([]);
  const [activeEditorIndex, setActiveEditorIndex] = useState<number | null>(null);
  const [selectedLinks, setSelectedLinks] = useState<string[]>([]);

  const handleLinkToggle = (link: string) => {
    setSelectedLinks((prev) =>
      prev.includes(link) ? prev.filter((l) => l !== link) : [...prev, link]
    );
  };

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  const urlParams = useQuery();

  const type = urlParams.get("type");
  const isFor = urlParams.get("isFor");
  const curRawNotificationTemplateId = urlParams.get("q");
  let curNotificationTemplateId = 0;

  const hasAllParams = curRawNotificationTemplateId && type && isFor;

  if (hasAllParams) {
    const decId = edService.decrypt(curRawNotificationTemplateId);
    if (decId) {
      curNotificationTemplateId = +decId;
    }
  } else {
    if (!isForPopUp) {
      navigate("/not-found");
    }
  }

  const editorRef = useRef<SimpleRichBoxHandle>(null);


  const setDisplayName = (value: string) => {
    if (editorRef.current) {
      editorRef.current.insertTextAtCursor(`{${value}}`);
    }
  };

  // async function setDisplayName(value: string) {
  //   setEditor(value);
  // }

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // DEFAULT SCHEMA VALUE FOR FORM
  const initNotificationTemplate: NotificationTemplate = {
    consumerId: userContext?.consumerId || 0,
    organizationId: userContext?.organizationId || 0,
    branchId: userContext?.branchId || 0,
    departmentId:
      userContext?.departmentId > 0
        ? {
          label: userContext?.departmentName || "",
          value: userContext?.departmentId?.toString(),
        }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
          label: userContext?.specialityName || "",
          value: userContext?.specialityId?.toString(),
        }
        : null,
    isDeleted: false,
    templateName: "",
    mediaType: curNotificationTemplateId <= 0
      ? (location?.state?.mediaType || type)
      : "",
    templateFor: curNotificationTemplateId <= 0
      ? (location?.state?.templateFor || isFor)
      : "",

    // mediaType: curNotificationTemplateId <= 0 ? location?.state?.mediaType : "",
    // templateFor:
    //   curNotificationTemplateId <= 0 ? location?.state?.templateFor : "",
    // mediaType: curNotificationTemplateId <= 0 ? mediaTypes : "",
    // templateFor: curNotificationTemplateId <= 0 ? templateFor : "",
    notificationTemplateId: 0,
    templateData: "",
    isLocked: false,
    isDefault: false,
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    notificationTemplateId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    organizationId: Yup.number().required(),
    branchId: Yup.number().required(),
    templateName: Yup.string().required(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    mediaType: Yup.string().required(),
    templateFor: Yup.string().required(),
    isDeleted: Yup.boolean().required(),
    isLocked: Yup.boolean().required(),
    isDefault: Yup.boolean().required(),
    templateData: Yup.string().required("Template content is required."),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initNotificationTemplate, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });
  const mediaType = methods.getValues()?.mediaType;
  const Result = { isLocked: true };

  const isLocked = Result?.isLocked || false;
  //DEPARTMENT LIST
  const getDepartmentList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("ConsumerId", userContext?.consumerId.toString());
      params.append("OrgId", userContext?.organizationId.toString());
      params.append("BranchId", userContext?.branchId.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetDepartmentList(params);
      if (result?.data?.data?.departmentColl) {
        const resultData = result?.data?.data.departmentColl;
        setDepartmentData(resultData);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  //Speciality LIST
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setSpecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const getNotificationTemplateDBList = async () => {
    try {
      let params = new URLSearchParams();
      // params.append("underModule", "");
      // params.append("templateType", "");
      // params.append("underModule", (isFor || "").toUpperCase());
      params.append("templateType", (isFor || "").toUpperCase());

      const resultDataCollection: any = await GetNotificationTemplateDBTableFieldList(params);
      if (resultDataCollection?.data?.data) {
        let NotificationTemplateCollection = resultDataCollection.data?.data;
        setResultDataCollection(NotificationTemplateCollection);
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const isMasterTemplate = location?.state?.isMasterTemplate === true;
  // GET NOTIFICATION TEMPLATE DETAIL
  const getNotificationTemplateDetailById = async () => {

    let Result: any;
    let params = new URLSearchParams();
    params.append(
      "notificationTemplateId",
      curNotificationTemplateId?.toString()
    );
    params.append("isMaster", isMasterTemplate ? "true" : "false");
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("organizationId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());

    try {
      const res: any = await GetNotificationTemplateDetail(params);
      if (res?.data?.data) {
        Result = res.data.data;
        if (Result != null) {
          getNotificationTemplateDBList();
        }

        if (curNotificationTemplateId > 0) {
          methods.reset({
            ...initNotificationTemplate,
            notificationTemplateId: Result?.notificationTemplateId || 0,
            templateName: Result?.templateName || "",
            departmentId: Result?.departmentId
              ? {
                label: Result?.departmentName,
                value: Result?.departmentId,
              }
              : null,
            specialityId: Result?.specialityId
              ? {
                label: Result?.specialityName,
                value: Result?.specialityId,
              }
              : null,
            mediaType: Result?.mediaType || "",
            templateFor: Result?.templateFor || "",
            templateData: Result?.templateData || "",
            isDeleted: Result?.isDeleted || false,
            isLocked: Result?.isLocked || false,
            isDefault: Result?.isDefault || false,
          });
          setSelectedLinks(
            Result?.templateLinks
              ? Result.templateLinks
                .split(",")
                .map((l: string) => l.trim())
                .filter((l: string) => availableLinks.includes(l))
              : []
          );
        } else {
          methods.reset({
            ...initNotificationTemplate,
            departmentId: methods.getValues()?.departmentId,
            specialityId: methods.getValues()?.specialityId,
          });
        }
      }
      return Result;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE NotificationTemplate
  const saveNotificationTemplate = async (value: NotificationTemplate) => {

    try {
      let result: any = "";

      let formData = {
        notificationTemplateId: value.notificationTemplateId,
        consumerId: userContext?.consumerId,
        organizationId: userContext?.organizationId,
        branchId: userContext?.branchId,
        templateName: value?.templateName?.trim() || "",
        specialityId: value.specialityId?.value || 0,
        departmentId: value.departmentId?.value || 0,
        mediaType: value?.mediaType || "",
        templateFor: value?.templateFor || "",
        templateData: value?.templateData || "",
        templateLinks: value?.mediaType === "EMAIL" ? selectedLinks.join(",") : "",
        isDeleted: value?.isDeleted || false,
        isLocked: value?.isLocked || false,
        isDefault: value?.isDefault || false,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (curNotificationTemplateId > 0) {
        result = await UpdateNotificationTemplate(formData);
      } else {
        result = await CreateNotificationTemplate(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result.data) {
            handleClosePopUp("RELATION_TO_FOOD_CREATE", result.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {

    await saveNotificationTemplate(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Always fetch displayName list regardless of Add/Edit mode
      await getNotificationTemplateDBList();

      // For Add mode
      if (curNotificationTemplateId <= 0) {
        await getDepartmentList();
      }

      // For Edit mode or when userContext exists
      if (curNotificationTemplateId > 0) {
        await getNotificationTemplateDetailById();
      } else if (userContext?.departmentId > 0) {
        await fetchSpeciality(userContext?.departmentId?.toString());
        if (userContext?.specialityId > 0) {
          await getNotificationTemplateDetailById();
        }
      } else {
        await getNotificationTemplateDetailById();
      }

      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error("Error during fetchData:", e);
    }
  };


  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curNotificationTemplateId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} md={6} lg={5}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.templateName
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Tempalte Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          autoFocus
                          size="small"
                          disabled={isMasterTemplate}
                          {...methods.register("templateName")}
                          error={!!methods.formState.errors.templateName}
                          className={`text-input ${methods.formState.errors.templateName ? "error" : ""
                            }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.templateName?.message}
                    </Typography>
                  </Grid>
                  <Grid item md={6}></Grid>
                  {!isMasterTemplate && (<Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.departmentId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={curNotificationTemplateId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setSpecialityData([]);
                                  }
                                }
                              }}
                              className={`text-input ${methods.formState.errors.departmentId
                                ? "error"
                                : ""
                                }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>)}
                  {!isMasterTemplate && (<Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.specialityId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              openMenuOnFocus
                              isClearable
                              isDisabled={curNotificationTemplateId > 0}
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                methods.setValue(
                                  "specialityId",
                                  e?.value ? e : null,
                                  {
                                    shouldValidate: true,
                                  }
                                );
                              }}
                              className={`text-input ${methods.formState.errors.specialityId
                                ? "error"
                                : ""
                                }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>)}
                  {!isMasterTemplate && (<Grid item xs={12}>
                    <Box
                      className="main-dialog-background-color"
                      sx={{
                        padding: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        overflow: "hidden",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        borderRadius: "8px",
                      }}
                    >
                      <Grid container spacing={2} wrap="wrap">
                        {resultDataCollection.map((text: any, index: number) => (
                          <Grid
                            key={index}
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            xl={12 / 5}
                          >
                            <Typography
                              sx={{
                                paddingRight: 1,
                                cursor: "pointer",
                                color: "blue",
                                width: "100%",
                                boxSizing: "border-box",
                                opacity: 1,
                              }}
                              onClick={() => {

                                setDisplayName(text.displayName);

                              }}
                            >
                              {` ${text.displayName} `}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>)}

                  {mediaType === "EMAIL" && !isMasterTemplate && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: "14px", mb: 1 }}>
                        Attachments
                      </Typography>
                      <Box
                        className="main-dialog-background-color"
                        sx={{
                          padding: "16px 18px",
                          display: "flex",
                          flexWrap: "wrap",
                          maxWidth: "100%",
                          boxSizing: "border-box",
                          borderRadius: "8px",
                        }}
                      >
                        <Grid container spacing={2} wrap="wrap">
                          {availableLinks?.map((link) => (
                            <Grid
                              key={link}
                              item
                              xs={12}
                              sm={6}
                              md={4}
                              lg={12 / 5}
                              sx={{ minWidth: 0 }}
                            >
                              <FormControlLabel
                                sx={{
                                  margin: 0,
                                  width: "100%",
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: "13px",
                                    whiteSpace: "nowrap",
                                  },
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    sx={{ padding: "0 8px 0 0" }}
                                    checked={selectedLinks?.includes(link)}
                                    onChange={() => handleLinkToggle(link)}
                                  />
                                }
                                label={link}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControl fullWidth className="from-controller">
                      <FormLabel className="fileds-label-text">
                        {mediaType} Template
                      </FormLabel>
                      <SkeletonWrapper isLoading={isLoading} className="skeleton-input">
                        <Controller
                          name="templateData"
                          control={methods.control}
                          defaultValue=""
                          render={({ field }) => (
                            mediaType === "SMS" ? (
                              <TextField
                                multiline
                                minRows={4}
                                maxRows={6}
                                fullWidth
                                value={field.value}
                                onChange={field.onChange}
                                disabled={isLocked}
                                placeholder="Enter SMS content"
                              />
                            ) : (
                              <Box className="tiptap-border">
                                <SimpleRichBox
                                  ref={editorRef}
                                  value={field.value}
                                  onChange={field.onChange}
                                  readOnly={isMasterTemplate}
                                  placeholder="Enter Content"
                                  onFocus={() => setActiveEditorIndex(0)}
                                />
                              </Box>
                            )
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors?.templateData?.message}
                      </Typography>
                    </FormControl>
                  </Grid>


                  {/* {methods
                    .getValues()
                    ?.notificationTemplateDetailColl?.map(
                      (item: NotificationTemplateDetail, index: number) => (
                        <Grid item xs={12} sm={6} md={6} key={index}>
                          <FormControl fullWidth className="from-controller">
                            <FormLabel className="fileds-label-text">
                              {" "}
                              {mediaType} Template - {item?.languageName}
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <Controller
                                name={`notificationTemplateDetailColl.${index}.templateData`}
                                control={methods.control}
                                defaultValue=""
                                render={({ field }) => (
                                  <Box className="tiptap-border">
                                    <TipTapTextEditor
                                      value={field.value} // CONTROLLED FORM VALUE
                                      onChange={field.onChange} // SYNC CHANGES WITH FORM
                                      setEditor={setEditor}
                                      editorContentClass="notification-tiptap-editor-content"
                                      toolbar={[
                                        "undo",
                                        "redo",
                                        "bold",
                                        "italic",
                                        "underline",
                                        "alignLeft",
                                        "alignCenter",
                                        "alignRight",
                                        "link",
                                        "table",
                                        "insertTable",
                                        "addRow",
                                        "deleteRow",
                                        "addColumn",
                                        "deleteColumn",
                                        "bulletList",
                                        "orderedList",
                                      ]}
                                    />
                                  </Box>
                                )}
                              />
                              <Typography className="validation-message">
                                {methods.formState.errors
                                  .notificationTemplateDetailColl &&
                                  methods.formState.errors
                                    .notificationTemplateDetailColl[index]
                                    ?.templateData?.message}
                              </Typography>
                            </SkeletonWrapper>
                          </FormControl>
                        </Grid>
                      )
                    )} */}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  {/* <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading}
                    onClick={() => {
                      console.log(methods.formState.errors);
                    }}
                  >
                    {curNotificationTemplateId > 0 ? "Update" : "Save"}
                  </LoadingButton> */}
                  {!isMasterTemplate && (
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="contained"
                      type="submit"
                      className="primary-button"
                      name="save"
                      disabled={isLoading}
                    >
                      {curNotificationTemplateId > 0 ? "Update" : "Save"}
                    </LoadingButton>
                  )}
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
